import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import { useAuth } from './AuthContext.jsx';
import { MEMBER_SECRET_KEY, FAMILY_CODE_KEY, DEVICE_UUID_KEY } from '../constants.js';

const FamilyContext = createContext(null);

function getDeviceUuid() {
  let uuid = localStorage.getItem(DEVICE_UUID_KEY);
  if (!uuid && typeof crypto !== 'undefined' && crypto.randomUUID) {
    uuid = crypto.randomUUID();
    localStorage.setItem(DEVICE_UUID_KEY, uuid);
  } else if (!uuid) {
     // Fallback for older browsers
     uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
      localStorage.setItem(DEVICE_UUID_KEY, uuid);
  }
  return uuid;
}

export function FamilyProvider({ children }) {
  const { token, setSession } = useAuth();
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) { // Don't check user, token is enough (guest mode)
      setFamily(null);
      setMembers([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
        const payload = await apiFetch('/api/family/my', { token });
        setFamily(payload.family);
        setMembers(payload.members || []);
    } catch (e) {
        console.error("Failed to refresh family", e);
    } finally {
        setIsLoading(false);
    }
  }, [token]);

  const createFamily = useCallback(async ({ name, member_name, secret }) => {
    const device_uuid = getDeviceUuid();
    const payload = await apiFetch('/api/family/create', { 
        token, 
        method: 'POST', 
        body: { 
            name, 
            member_name,
            member_secret: secret, 
            device_uuid 
        } 
    });
    
    if (payload.token) {
        localStorage.setItem(MEMBER_SECRET_KEY, secret); 
        localStorage.setItem(FAMILY_CODE_KEY, payload.family.invite_code);
        setSession({ token: payload.token, member: payload.member });
    }
    return payload;
  }, [setSession, token]);

  const joinFamily = useCallback(async ({ invite_code, member_name, secret }) => {
    const device_uuid = getDeviceUuid();
    const payload = await apiFetch('/api/family/join', { 
        token, 
        method: 'POST', 
        body: { 
            invite_code, 
            member_name,
            member_secret: secret, 
            device_uuid 
        } 
    });
    
    if (payload.token) {
        localStorage.setItem(MEMBER_SECRET_KEY, secret);
        localStorage.setItem(FAMILY_CODE_KEY, payload.family.invite_code);
        setSession({ token: payload.token, member: payload.member });
    }
    return payload;
  }, [setSession, token]);

  const reconnect = useCallback(async ({ invite_code, member_secret }) => {
     const payload = await apiFetch('/api/family/reconnect', { method: 'POST', body: { invite_code, member_secret } });
     if (payload.token) {
        setSession({ token: payload.token, member: payload.member });
        // If secret provided, update local storage
        if (member_secret) {
            localStorage.setItem(MEMBER_SECRET_KEY, member_secret);
        }
        if (invite_code) {
             localStorage.setItem(FAMILY_CODE_KEY, invite_code);
        }
     }
     return payload;
  }, [setSession]);

  const requestMagicLink = useCallback(async ({ email }) => {
    return apiFetch('/api/family/magic-link/request', {
      method: 'POST',
      body: { email }
    });
  }, []);

  useEffect(() => {
    refresh().catch(() => setIsLoading(false));
  }, [refresh]); 

  const value = useMemo(
    () => ({ family, members, isLoading, refresh, createFamily, joinFamily, reconnect, requestMagicLink }),
    [createFamily, family, isLoading, joinFamily, members, reconnect, refresh, requestMagicLink]
  );

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}

export function useFamily() {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error('useFamily must be used within FamilyProvider');
  return ctx;
}
